import { fetchJson } from './client';

export interface ProductPricingPreview {
  /** Stripe-derived trial length in days; null when unknown or endpoint unavailable (PIPH-227). */
  trialPeriodDays: number | null;
  /** Human-readable price for marketing (e.g. "$200 / year"), from Stripe via commerce. */
  formattedPriceLine: string | null;
  /** Short billing context (e.g. "Billed annually · Cancel anytime"). */
  billingIntervalLabel: string | null;
  /** Human-readable reason when live pricing could not be loaded. */
  unavailableReason: string | null;
}

interface RawProductPricing {
  trialPeriodDays?: unknown;
  formattedPriceLine?: unknown;
  billingIntervalLabel?: unknown;
}

interface RawPricingForIntent {
  unitAmount?: unknown;
  currency?: unknown;
  interval?: unknown;
  intervalCount?: unknown;
  trialPeriodDays?: unknown;
}

type RawPricingForResponse = Record<string, RawPricingForIntent | null | undefined>;
interface RawPricingForEnvelope {
  prices?: RawPricingForResponse;
}

function normalizeTrialDays(raw: unknown): number | null {
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return null;
  const days = Math.floor(raw);
  if (days <= 0 || days > 365) return null;
  return days;
}

function normalizeMarketingString(raw: unknown, maxLen = 160): string | null {
  if (typeof raw !== 'string') return null;
  const t = raw.trim().replace(/\s+/g, ' ');
  if (!t || t.length > maxLen) return null;
  return t;
}

function normalizeCurrency(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const t = raw.trim().toUpperCase();
  if (!/^[A-Z]{3}$/.test(t)) return null;
  return t;
}

function normalizeInterval(raw: unknown): 'day' | 'week' | 'month' | 'year' | null {
  return raw === 'day' || raw === 'week' || raw === 'month' || raw === 'year' ? raw : null;
}

function normalizeIntervalCount(raw: unknown): number {
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return 1;
  const n = Math.floor(raw);
  if (n <= 0 || n > 100) return 1;
  return n;
}

function normalizeUnitAmount(raw: unknown): number | null {
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return null;
  const n = Math.floor(raw);
  return n >= 0 ? n : null;
}

function formatIntervalLabel(interval: 'day' | 'week' | 'month' | 'year', intervalCount: number): string {
  if (intervalCount <= 1) return interval;
  return `${intervalCount} ${interval}s`;
}

function formatPriceLine(input: { unitAmount: number; currency: string; interval: 'day' | 'week' | 'month' | 'year' | null; intervalCount: number; }): string {
  const amount = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: input.currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(input.unitAmount / 100);
  if (!input.interval) return amount;
  return `${amount} / ${formatIntervalLabel(input.interval, input.intervalCount)}`;
}

const emptyPreview = (): ProductPricingPreview => ({
  trialPeriodDays: null,
  formattedPriceLine: null,
  billingIntervalLabel: null,
  unavailableReason: null,
});

/**
 * Fetches Stripe-derived preview fields from commerce `GET /pricingFor?productId=`.
 * Fails softly when endpoint is unavailable or shape is unexpected.
 */
export async function fetchProductPricingPreview(productId: string): Promise<ProductPricingPreview> {
  try {
    const raw = await fetchJson<RawPricingForResponse | RawPricingForEnvelope>({
      method: 'GET',
      relativePath: `/pricingFor?productId=${encodeURIComponent(productId)}`,
      requireAuth: false,
      errorContext: 'Product pricing preview',
    });
    // Current endpoint shape: { productId, prices: { yearly: {...}, ... } }.
    // Backward-compatible fallback: treat top-level object as the price-intent map.
    let pricingMap: RawPricingForResponse = {};
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      const maybeEnvelope = (raw as RawPricingForEnvelope).prices;
      pricingMap =
        maybeEnvelope && typeof maybeEnvelope === 'object' && !Array.isArray(maybeEnvelope)
          ? (maybeEnvelope as RawPricingForResponse)
          : (raw as RawPricingForResponse);
    }
    const yearly = pricingMap?.yearly;
    const firstIntent = yearly ?? Object.values(pricingMap ?? {}).find((entry) => entry && typeof entry === 'object');
    const unitAmount = normalizeUnitAmount(firstIntent?.unitAmount);
    const currency = normalizeCurrency(firstIntent?.currency);
    const interval = normalizeInterval(firstIntent?.interval);
    const intervalCount = normalizeIntervalCount(firstIntent?.intervalCount);
    const formattedPriceLine =
      unitAmount !== null && currency ? formatPriceLine({ unitAmount, currency, interval, intervalCount }) : null;
    const billingIntervalLabel =
      interval === null ? null : `Billed every ${formatIntervalLabel(interval, intervalCount)}`;

    // Backward-compatible fallback while old endpoint shape may still be present.
    const legacy = raw as unknown as RawProductPricing;
    return {
      trialPeriodDays: normalizeTrialDays(firstIntent?.trialPeriodDays ?? legacy?.trialPeriodDays),
      formattedPriceLine: formattedPriceLine ?? normalizeMarketingString(legacy?.formattedPriceLine),
      billingIntervalLabel: billingIntervalLabel ?? normalizeMarketingString(legacy?.billingIntervalLabel),
      unavailableReason: null,
    };
  } catch (err: unknown) {
    const fallback = emptyPreview();
    if (err instanceof Error && err.message.trim().length > 0) {
      fallback.unavailableReason = err.message.trim();
    }
    return fallback;
  }
}
